import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { authUtils } from '../../utils/auth';
import { API_ENDPOINTS } from '../../config/api';
import axios from 'axios';
import { CollapsibleSection } from './CollapsibleSection';
import { ProfileMatchButton } from './ProfileMatchButton';
import { ProfileMatchResult, ProfileMatchResultData } from './ProfileMatchResult';

interface GeneratedDocument {
  cv_link: string;
  cover_letter_link: string;
  generated_at: string;
}

interface HistoryEntry {
  timestamp: string;
  status: string;
}

interface JobCard {
  id: string;
  job_title: string;
  job_url: string;
  company_name: string;
  status: string;
  history: HistoryEntry[];
  generated_documents?: GeneratedDocument;
  applied_by?: 'user' | 'bot';
}

interface JobDetailsModalProps {
  job: JobCard;
  onClose: () => void;
  onDelete: () => void;
  onSaveGeneratedDocuments: (job: JobCard, cvLink: string, coverLetterLink: string) => Promise<void>;
}

const JobDetailsModal: React.FC<JobDetailsModalProps> = ({ job, onClose, onDelete, onSaveGeneratedDocuments }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedDocuments, setGeneratedDocuments] = useState<{
    resumeUrl?: string;
    coverLetterUrl?: string;
  }>(() => {
    // Initialize with existing documents if available
    if (job.generated_documents) {
      return {
        resumeUrl: job.generated_documents.cv_link,
        coverLetterUrl: job.generated_documents.cover_letter_link,
      };
    }
    return {};
  });
  const [profileMatchResult, setProfileMatchResult] = useState<ProfileMatchResultData | null>(null);

  const handleDelete = () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
    } else {
      onDelete();
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return 'Recently';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Viewed':
        return 'bg-gray-100 text-gray-700';
      case 'Applied':
        return 'bg-blue-100 text-blue-700';
      case 'Interview':
        return 'bg-yellow-100 text-yellow-700';
      case 'Offer':
        return 'bg-green-100 text-green-700';
      case 'Rejected':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const generateDocuments = async () => {
    setIsGenerating(true);
    try {
      const token = authUtils.getToken();
      
      // Step 1: Scrape job posting
      console.log('Step 1: Scraping job posting...');
      const jobResponse = await axios.post(
        API_ENDPOINTS.COMPANY_SCRAPPER.SCRAPE_JOB,
        { url: job.job_url },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!jobResponse.data.success || !jobResponse.data.id) {
        throw new Error('Failed to scrape job posting');
      }

      const jobId = jobResponse.data.id;
      console.log('Job ID:', jobId);

      // Step 2: Generate resume (without company)
      console.log('Step 2: Generating resume...');
      const resumeResponse = await axios.post(
        API_ENDPOINTS.DOCUMENT_GENERATOR.RESUME_WITHOUT_COMPANY,
        {
          job_id: jobId,
          company_name: job.company_name
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!resumeResponse.data.id) {
        throw new Error('Failed to generate resume');
      }

      const resumeId = resumeResponse.data.id;
      console.log('Resume ID:', resumeId);

      // Step 3: Generate cover letter (without company)
      console.log('Step 3: Generating cover letter...');
      const coverLetterResponse = await axios.post(
        API_ENDPOINTS.DOCUMENT_GENERATOR.COVER_LETTER_WITHOUT_COMPANY,
        {
          job_id: jobId,
          company_name: job.company_name
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!coverLetterResponse.data.id) {
        throw new Error('Failed to generate cover letter');
      }

      const coverLetterId = coverLetterResponse.data.id;
      console.log('Cover Letter ID:', coverLetterId);

      // Step 4: Generate resume PDF
      console.log('Step 4: Generating resume PDF...');
      const resumePdfResponse = await axios.post(
        API_ENDPOINTS.PDF_GENERATOR.GENERATE_RESUME_PDF,
        { resume_id: resumeId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!resumePdfResponse.data.success || !resumePdfResponse.data.url) {
        throw new Error('Failed to generate resume PDF');
      }

      // Step 5: Generate cover letter PDF
      console.log('Step 5: Generating cover letter PDF...');
      const coverLetterPdfResponse = await axios.post(
        API_ENDPOINTS.PDF_GENERATOR.GENERATE_COVER_LETTER_PDF,
        { letter_id: coverLetterId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!coverLetterPdfResponse.data.success || !coverLetterPdfResponse.data.url) {
        throw new Error('Failed to generate cover letter PDF');
      }

      // Step 6: Set the URLs for download
      const cvUrl = resumePdfResponse.data.url;
      const coverLetterUrl = coverLetterPdfResponse.data.url;
      
      setGeneratedDocuments({
        resumeUrl: cvUrl,
        coverLetterUrl: coverLetterUrl
      });

      // Step 7: Save generated documents to backend
      await onSaveGeneratedDocuments(job, cvUrl, coverLetterUrl);

      console.log('Documents generated successfully!');
      
    } catch (error: any) {
      console.error('Failed to generate documents:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);

      // Check for quota exceeded error (403)
      if (error.response?.status === 403) {
        const errorData = error.response?.data;
        const quotaError = errorData?.error || errorData?.message || 'Your subscription quota has been exceeded. Please upgrade your plan to continue.';

        // Show a user-friendly modal or alert
        alert(`❌ Quota Exceeded\n\n${quotaError}\n\nPlease upgrade your subscription or wait for your quota to renew.`);
      } else if (error.response?.status === 401) {
        // Authentication error - show error but let the app handle token refresh
        alert('Your session has expired. Please log in again.');
      } else {
        // Other errors
        const errorData = error.response?.data;
        const errorMessage = errorData?.error || errorData?.message || error.message || 'Failed to generate documents. Please try again.';
        alert(`❌ Error\n\n${errorMessage}`);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-jobesta shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-jobesta-gray-90 mb-2">
                {job.job_title}
              </h2>
              <p className="text-jobesta-gray-70">{job.company_name}</p>
              {job.applied_by === 'bot' && (
                <span
                  className="mt-2 inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-700"
                  title="This application was submitted by the auto-apply bot"
                >
                  🤖 Auto-applied by bot
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-jobesta-gray-60 hover:text-jobesta-gray-90"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-jobesta-gray-70 mb-2">
              Job URL
            </label>
            <a
              href={job.job_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-jobesta-blue hover:underline break-all"
            >
              {job.job_url}
            </a>
          </div>

          <div className="mb-6">
            <CollapsibleSection title="Application History" defaultExpanded={false}>
              <div className="space-y-3">
                {job.history.map((entry, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-jobesta-gray-30 rounded-jobesta"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(entry.status)}`}>
                        {entry.status}
                      </span>
                    </div>
                    <span className="text-sm text-jobesta-gray-60">
                      {formatTimestamp(entry.timestamp)}
                    </span>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          </div>

          {/* Profile Match Section */}
          <div className="mb-6">
            <CollapsibleSection title="Profile Match Analysis" defaultExpanded={false}>
              {!profileMatchResult ? (
                <ProfileMatchButton
                  jobUrl={job.job_url}
                  onMatchResult={setProfileMatchResult}
                  authToken={authUtils.getToken() || ''}
                />
              ) : (
                <div className="space-y-4">
                  <ProfileMatchResult result={profileMatchResult} />
                  <button
                    onClick={() => setProfileMatchResult(null)}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                  >
                    Analyze Again
                  </button>
                </div>
              )}
            </CollapsibleSection>
          </div>

          {/* CV and Cover Letter Generation Section */}
          <div className="mb-6 p-4 bg-blue-50 rounded-jobesta border border-blue-200">
            <h3 className="text-sm font-medium text-jobesta-gray-70 mb-3">
              Generate Documents
            </h3>
            {!generatedDocuments.resumeUrl && !generatedDocuments.coverLetterUrl ? (
              <button
                onClick={generateDocuments}
                disabled={isGenerating}
                className="w-full px-4 py-2 bg-jobesta-blue text-white rounded-jobesta hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating CV and Cover Letter...
                  </span>
                ) : (
                  'Generate CV and Cover Letter'
                )}
              </button>
            ) : (
              <div className="space-y-2">
                {generatedDocuments.resumeUrl && (
                  <a
                    href={generatedDocuments.resumeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full px-4 py-2 bg-green-600 text-white rounded-jobesta hover:bg-green-700 transition-colors text-center"
                  >
                    Download CV
                  </a>
                )}
                {generatedDocuments.coverLetterUrl && (
                  <a
                    href={generatedDocuments.coverLetterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full px-4 py-2 bg-green-600 text-white rounded-jobesta hover:bg-green-700 transition-colors text-center"
                  >
                    Download Cover Letter
                  </a>
                )}
                <button
                  onClick={generateDocuments}
                  disabled={isGenerating}
                  className="w-full px-4 py-2 bg-jobesta-gray-40 text-jobesta-gray-70 rounded-jobesta hover:bg-jobesta-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  Regenerate Documents
                </button>
                {job.generated_documents?.generated_at && (
                  <p className="text-xs text-gray-600 mt-2">
                    Generated: {new Date(job.generated_documents.generated_at).toLocaleString()}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-between">
            {!showDeleteConfirm ? (
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-jobesta-red text-white rounded-jobesta hover:bg-opacity-90 transition-colors"
              >
                Delete Job
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm text-jobesta-gray-70">Are you sure?</span>
                <button
                  onClick={handleDelete}
                  className="px-3 py-1 bg-jobesta-red text-white rounded-jobesta text-sm hover:bg-opacity-90"
                >
                  Confirm
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-3 py-1 bg-jobesta-gray-40 text-jobesta-gray-70 rounded-jobesta text-sm hover:bg-jobesta-gray-50"
                >
                  Cancel
                </button>
              </div>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-jobesta-gray-40 text-jobesta-gray-70 rounded-jobesta hover:bg-jobesta-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetailsModal;